import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import {
  IconButton,
  LoadingIndicator,
  Tooltip,
  useAlert,
  useDialog
} from "@digital-services/ui";

import { useGetDefaultOrganization } from "@account/services/use-get-default-organization";
import { useSetDefaultOrganization } from "@account/services/use-set-default-organization";
import { shortenOrgName } from "@organization/utils";

import { Organization } from "@hooks/useOrganizationContext/models/Organization";
import ErrorIcon from "@icons/error.svg?react";
import StarIcon from "@icons/star.svg?react";
import StarStrokeIcon from "@icons/starStroke.svg?react";
import { defaultTriggerDialogOptions } from "@utils/common";

interface Props {
  organization: Organization;
}

const DefaultOrganizationToggleButton: FunctionComponent<Props> = ({
  organization
}) => {
  const { update: setDefaultOrganization } = useSetDefaultOrganization();
  const { defaultOrganizationId, isLoading, error } =
    useGetDefaultOrganization();

  const { t } = useTranslation(undefined, { keyPrefix: "organization" });
  const { t: tCommon } = useTranslation();
  const { showDialog } = useDialog();
  const { notifySuccess, notifyError } = useAlert();

  function onSetDefaultOrganization(organization: Organization) {
    const shortName = shortenOrgName(organization.name);

    showDialog({
      variant: "warning",
      header: t("default.confirm.title"),
      content: t("default.confirm.message", {
        organization: shortName
      }),
      confirmButton: tCommon("button.confirm.label"),
      onConfirm: async () => {
        const response = await setDefaultOrganization(organization.id);
        const toastOptions = {
          organization: shortName
        };

        if (response?.isSuccess) {
          notifySuccess({
            header: t("default.success.message", toastOptions),
            "data-testid": "set-default-organization-success"
          });
        } else {
          notifyError({
            header: t("default.error.message", toastOptions),
            "data-testid": "set-default-organization-error"
          });
        }
      },
      cancelButton: tCommon("button.cancel.label"),
      "data-testid": "set-default-organization-confirmation",
      ...defaultTriggerDialogOptions
    });
  }
  if (isLoading) return <LoadingIndicator />;

  if (error) {
    return (
      <Tooltip message={t("load.error.message")} placement="bottom-left">
        <div data-testid="error-icon">
          <ErrorIcon className="m-3 h-3 max-w-none sm:h-4" />
        </div>
      </Tooltip>
    );
  }

  return (
    <>
      {defaultOrganizationId === organization.id && (
        <StarIcon
          data-testid="star-icon"
          className="m-3 mt-1 h-3 max-w-none sm:h-4"
        />
      )}
      {defaultOrganizationId !== organization.id && (
        <div className="group/edit invisible group-hover/item:visible">
          <Tooltip
            message={t("default.tooltip", {
              organization: shortenOrgName(organization.name)
            })}
            placement="bottom-left"
            data-testid="set-default-tooltip"
          >
            <IconButton
              data-testid={`set-default-${organization.id}`}
              onClick={() => {
                onSetDefaultOrganization(organization);
              }}
            >
              <StarStrokeIcon className="m-1 h-3 sm:h-4" />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </>
  );
};

export default DefaultOrganizationToggleButton;
